import { createContext, useContext, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '../api/fetch';
import { useAuth } from './AuthContext';

export interface OnboardingState {
  emailDone: boolean;
  digestDone: boolean;
  chatTaskDone: boolean;
  breakdownDone: boolean;
  calendarDone: boolean;
  wizardComplete: boolean;
  onboardingComplete: boolean;
}

const INITIAL: OnboardingState = {
  emailDone: false,
  digestDone: false,
  chatTaskDone: false,
  breakdownDone: false,
  calendarDone: false,
  wizardComplete: false,
  onboardingComplete: false,
};

interface OnboardingContextValue extends OnboardingState {
  isLoading: boolean;
  /**
   * Fire a refetch and resolve with the fresh state. Callers that need to act
   * on the result immediately (e.g. wizard "I replied — check now") can await
   * this and inspect `emailDone` directly instead of racing against a re-render.
   * Returns the previous data on error.
   */
  refetch: () => Promise<OnboardingState>;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Scope the cache by userId so a fresh signup never sees the previous user's
  // wizardComplete flag (which caused a dashboard → wizard flash). Disable the
  // query entirely while unauthenticated.
  const { data, isLoading, isFetching, refetch } = useQuery<OnboardingState>({
    queryKey: ['onboarding', userId],
    queryFn: () => fetchApi('/onboarding'),
    enabled: !!userId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return 3000;
      if (!d.wizardComplete) return 3000;
      if (!d.onboardingComplete) return 10000;
      return false;
    },
    staleTime: 0,
    retry: false,
  });

  // While the user is authenticated but we haven't received the first response,
  // treat the state as "loading" so route guards don't act on the INITIAL
  // defaults. Without this, WizardGuard would briefly see wizardComplete=false
  // and bounce to /onboarding, OR (worse) the dashboard would flash through
  // for one frame before the redirect.
  const effectiveLoading = !!userId && (isLoading || (isFetching && !data));

  const value: OnboardingContextValue = {
    ...(data ?? INITIAL),
    isLoading: effectiveLoading,
    refetch: useCallback(async () => {
      const result = await refetch();
      return result.data ?? data ?? INITIAL;
    }, [refetch, data]),
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
}
